import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, LogOut, Globe, FileText, Calendar, Image as ImageIcon, Receipt, User, ShieldCheck, ChevronRight, Inbox } from 'lucide-react';

import MyProfileCard from './dashboard/MyProfileCard';
import { MyQuotesList, MyInvoicesList } from './dashboard/MyDocuments';
import MyWarrantiesList from './dashboard/MyWarrantiesList';
import MyPhotosGrid from './dashboard/MyPhotosGrid';
import MyProjectsList from './dashboard/MyProjectsList';
import PhotoLightbox from './dashboard/PhotoLightbox';
import InboxTab from './dashboard/InboxTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const fmtEuro = (v) => `€ ${(Number(v) || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ClientDashboard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Data
  const [requests, setRequests] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [myQuotes, setMyQuotes] = useState([]);
  const [myInvoices, setMyInvoices] = useState([]);
  const [myWarranties, setMyWarranties] = useState([]);
  const [inbox, setInbox] = useState({ conversations: [], unread_count: 0 });
  const [profile, setProfile] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Profile edit
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', email: '', phone: '', address: '', company_name: '' });
  const [savingProfile, setSavingProfile] = useState(false);

  // UI
  const [activeTab, setActiveTab] = useState('overzicht');
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });

  useEffect(() => {
    const token = localStorage.getItem('clientToken');
    const data = localStorage.getItem('clientData');
    if (!token) {
      navigate('/client');
      return;
    }
    if (data) setClientData(JSON.parse(data));
    fetchAll(token);
  }, [navigate]);

  const fetchAll = async (token) => {
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const [reqs, ps, qs, invs, prof, ws, inb] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/client/work-requests`, auth),
        axios.get(`${BACKEND_URL}/api/client/me/photos`, auth).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/client/me/quotes`, auth).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/client/me/invoices`, auth).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/client/me/profile`, auth).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/client/me/warranties`, auth).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/client/me/inbox`, auth).catch(() => ({ data: null })),
      ]);
      setRequests(reqs.data || []);
      setPhotos(ps.data || []);
      setMyQuotes(qs.data || []);
      setMyInvoices(invs.data || []);
      setMyWarranties(ws.data || []);
      setInbox(inb.data || { conversations: [], unread_count: 0 });
      if (prof.data) {
        setProfile(prof.data);
        setProfileForm({
          name: prof.data.name || '',
          email: prof.data.email || '',
          phone: prof.data.phone || '',
          address: prof.data.address || '',
          company_name: prof.data.company_name || '',
        });
      }
    } catch (e) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const refreshInbox = async () => {
    const token = localStorage.getItem('clientToken');
    if (!token) return;
    try {
      const r = await axios.get(`${BACKEND_URL}/api/client/me/inbox`, { headers: { Authorization: `Bearer ${token}` } });
      setInbox(r.data || { conversations: [], unread_count: 0 });
    } catch { /* ignore */ }
  };

  const markInboxSeen = async () => {
    const token = localStorage.getItem('clientToken');
    if (!token) return;
    try {
      await axios.post(`${BACKEND_URL}/api/client/me/inbox/seen`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setInbox((s) => ({ ...s, unread_count: 0 }));
    } catch { /* ignore */ }
  };

  const handleSaveProfile = async () => {
    const token = localStorage.getItem('clientToken');
    if (!token) return;
    setSavingProfile(true);
    try {
      const r = await axios.put(`${BACKEND_URL}/api/client/me/profile`, profileForm, { headers: { Authorization: `Bearer ${token}` } });
      if (r.data?.profile) setProfile(r.data.profile);
      toast.success(i18n.language === 'nl' ? 'Gegevens bijgewerkt' : 'Profile updated');
      setEditingProfile(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('clientToken');
    localStorage.removeItem('clientData');
    // If admin was impersonating, restore admin session and go back to admin
    const adminReturnToken = localStorage.getItem('admin_return_token');
    if (adminReturnToken) {
      localStorage.setItem('token', adminReturnToken);
      localStorage.removeItem('admin_return_token');
      window.location.href = '/klantenportaal-users';
      return;
    }
    navigate('/client');
  };

  const isImpersonating = !!localStorage.getItem('admin_return_token');

  const toggleLanguage = () => i18n.changeLanguage(i18n.language === 'nl' ? 'en' : 'nl');

  // Cross-origin fast download (used by lightbox + photo cards)
  const downloadFromUrl = async (url, filename) => {
    try {
      const resp = await fetch(url, { mode: 'cors' });
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch {
      window.open(url, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a2a2a] via-[#0d3d3d] to-[#1a5656] flex items-center justify-center">
        <div className="text-white text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a2a2a] via-[#0d3d3d] to-[#1a5656] p-4 md:p-8">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">Cardoso Bouw</h1>
            <p className="text-white/70 text-lg">{t('welcome')}, {clientData?.name}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => window.open('https://cardosobouw.nl', '_blank')} className="text-white hover:bg-white/10">
              {i18n.language === 'nl' ? 'Naar Website' : 'To Website'}
            </Button>
            <Button variant="ghost" size="sm" onClick={toggleLanguage} className="text-white hover:bg-white/10">
              <Globe className="w-4 h-4 mr-2" /> {i18n.language === 'nl' ? 'EN' : 'NL'}
            </Button>
            <Button variant="ghost" onClick={handleLogout} className="text-white hover:bg-white/10">
              <LogOut className="w-4 h-4 mr-2" /> {t('logout')}
            </Button>
          </div>
        </div>

        <Card className="bg-white/10 border-white/20 backdrop-blur mb-6">
          <CardContent className="py-6">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <img
                src="https://customer-assets.emergentagent.com/job_dreamy-mcnulty-2/artifacts/39goexey_JPEG-afbeelding-4738-BFE8-D0-0.jpeg"
                alt="Cardoso Bouw Logo"
                className="w-24 h-24 object-contain flex-shrink-0"
              />
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  {i18n.language === 'nl' ? 'Cardoso Bouw en Onderhoud' : 'Cardoso Construction & Maintenance'}
                </h2>
                <p className="text-white/90 text-base leading-relaxed">
                  {i18n.language === 'nl'
                    ? 'Bij Cardoso Bouw en Onderhoud gaat het niet alleen om bouwen – het gaat om vertrouwen, vakmanschap en de zekerheid dat uw droomhuis in de beste handen zijn.'
                    : "At Cardoso Construction & Maintenance it's not just about building – it's about trust, craftsmanship and the certainty that your dream home is in the best hands."}
                </p>
                <p className="text-white/80 text-sm mt-2">
                  <strong>{i18n.language === 'nl' ? 'Onze diensten:' : 'Our services:'}</strong> Aannemer • Renovatie • Stukadoors • Schilderswerk • Timmerwerk • Badkamer Specialist • Onderhoud
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Link to="/client/new-request">
          <Button className="bg-[#3da5a5] hover:bg-[#2d8585] text-white font-semibold px-6 py-6 rounded-xl" data-testid="new-request-btn">
            <Plus className="w-5 h-5 mr-2" /> {t('newRequest')}
          </Button>
        </Link>
      </div>

      {/* Impersonation banner — shown only when admin is logged in as klant */}
      {isImpersonating && (
        <div className="max-w-7xl mx-auto mb-3" data-testid="impersonation-banner">
          <div className="bg-purple-600 text-white rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm font-semibold flex items-center gap-2">
              <span className="text-lg">👁️</span>
              Admin-modus: u bekijkt het portaal als <strong>{clientData?.name || clientData?.email}</strong>
            </p>
            <button
              type="button"
              onClick={handleLogout}
              className="bg-white text-purple-700 hover:bg-purple-50 px-4 py-1.5 rounded font-semibold text-xs whitespace-nowrap"
              data-testid="exit-impersonation-btn"
            >
              ← Terug naar admin
            </button>
          </div>
        </div>
      )}

      {/* Tabbed content */}
      <div className="max-w-7xl mx-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full flex flex-wrap h-auto bg-[#0d3d3d]/60 border border-white/10 mb-6 p-1 gap-1 justify-start">
            <TabsTrigger value="overzicht" className="data-[state=active]:bg-[#3da5a5] data-[state=active]:text-white text-white/70" data-testid="tab-overzicht">
              <FileText className="w-4 h-4 mr-1.5" /> {i18n.language === 'nl' ? 'Overzicht' : 'Overview'}
            </TabsTrigger>
            <TabsTrigger value="inbox" className="data-[state=active]:bg-[#3da5a5] data-[state=active]:text-white text-white/70 relative" data-testid="tab-inbox">
              <Inbox className="w-4 h-4 mr-1.5" /> Inbox
              {inbox.unread_count > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center text-[10px] font-bold bg-amber-400 text-amber-900 rounded-full px-1.5 min-w-[18px]" data-testid="inbox-badge">{inbox.unread_count}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="offertes" className="data-[state=active]:bg-[#3da5a5] data-[state=active]:text-white text-white/70" data-testid="tab-offertes">
              <FileText className="w-4 h-4 mr-1.5" /> {i18n.language === 'nl' ? 'Offertes' : 'Quotes'} ({myQuotes.length})
            </TabsTrigger>
            <TabsTrigger value="facturen" className="data-[state=active]:bg-[#3da5a5] data-[state=active]:text-white text-white/70" data-testid="tab-facturen">
              <Receipt className="w-4 h-4 mr-1.5" /> {i18n.language === 'nl' ? 'Facturen' : 'Invoices'} ({myInvoices.length})
            </TabsTrigger>
            <TabsTrigger value="garanties" className="data-[state=active]:bg-[#3da5a5] data-[state=active]:text-white text-white/70" data-testid="tab-garanties">
              <ShieldCheck className="w-4 h-4 mr-1.5" /> {i18n.language === 'nl' ? 'Garanties' : 'Warranties'} ({myWarranties.length})
            </TabsTrigger>
            <TabsTrigger value="fotos" className="data-[state=active]:bg-[#3da5a5] data-[state=active]:text-white text-white/70" data-testid="tab-fotos">
              <ImageIcon className="w-4 h-4 mr-1.5" /> {i18n.language === 'nl' ? "Foto's" : 'Photos'} ({photos.length})
            </TabsTrigger>
            <TabsTrigger value="projecten" className="data-[state=active]:bg-[#3da5a5] data-[state=active]:text-white text-white/70" data-testid="tab-projecten">
              <Calendar className="w-4 h-4 mr-1.5" /> {i18n.language === 'nl' ? 'Projecten' : 'Projects'} ({requests.length})
            </TabsTrigger>
            <TabsTrigger value="gegevens" className="data-[state=active]:bg-[#3da5a5] data-[state=active]:text-white text-white/70" data-testid="tab-gegevens">
              <User className="w-4 h-4 mr-1.5" /> {i18n.language === 'nl' ? 'Mijn gegevens' : 'My Details'}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overzicht" className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: i18n.language === 'nl' ? 'Inbox' : 'Inbox', count: inbox.unread_count, icon: <Inbox className="w-5 h-5" />, tab: 'inbox' },
                { label: i18n.language === 'nl' ? 'Offertes' : 'Quotes', count: myQuotes.length, icon: <FileText className="w-5 h-5" />, tab: 'offertes' },
                { label: i18n.language === 'nl' ? 'Facturen' : 'Invoices', count: myInvoices.length, icon: <Receipt className="w-5 h-5" />, tab: 'facturen' },
                { label: i18n.language === 'nl' ? "Foto's" : 'Photos', count: photos.length, icon: <ImageIcon className="w-5 h-5" />, tab: 'fotos' },
              ].map((s, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveTab(s.tab)}
                  className="bg-[#0d3d3d]/80 border border-white/10 hover:border-[#3da5a5]/60 rounded-xl p-4 text-left transition-all group cursor-pointer hover:scale-[1.02]"
                  data-testid={`overview-stat-${s.tab}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[#3da5a5]">{s.icon}</div>
                    <ChevronRight className="w-4 h-4 text-white/40 group-hover:text-[#3da5a5]" />
                  </div>
                  <p className="text-3xl font-bold text-white">{s.count}</p>
                  <p className="text-xs text-white/60 mt-1">{s.label}</p>
                </button>
              ))}
            </div>
            <Card className="bg-[#0d3d3d]/80 border-white/10">
              <CardContent className="p-5 space-y-3">
                <p className="text-sm text-white/80">
                  {i18n.language === 'nl'
                    ? "👋 Welkom in uw klantenportaal. Klik op een tabblad om uw offertes, facturen, garanties of foto's te bekijken."
                    : '👋 Welcome to your client portal. Click a tab to view your quotes, invoices, warranties, or photos.'}
                </p>
                <p className="text-xs text-white/50">
                  {i18n.language === 'nl'
                    ? 'Tip: open een offerte of factuur om vragen te stellen en commentaar te geven — wij antwoorden meestal binnen 1 werkdag.'
                    : 'Tip: open a quote or invoice to ask questions and leave comments — we usually reply within 1 business day.'}
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="inbox" className="mt-0">
            <InboxTab inbox={inbox} navigate={navigate} i18n={i18n} markSeen={markInboxSeen} refreshInbox={refreshInbox} />
          </TabsContent>

          <TabsContent value="gegevens" className="mt-0">
            <MyProfileCard
              profile={profile}
              editingProfile={editingProfile}
              setEditingProfile={setEditingProfile}
              profileForm={profileForm}
              setProfileForm={setProfileForm}
              savingProfile={savingProfile}
              handleSaveProfile={handleSaveProfile}
              i18n={i18n}
            />
          </TabsContent>

          <TabsContent value="offertes" className="mt-0">
            <MyQuotesList quotes={myQuotes} navigate={navigate} i18n={i18n} fmtEuro={fmtEuro} />
          </TabsContent>

          <TabsContent value="facturen" className="mt-0">
            <MyInvoicesList invoices={myInvoices} navigate={navigate} i18n={i18n} fmtEuro={fmtEuro} />
          </TabsContent>

          <TabsContent value="garanties" className="mt-0">
            <MyWarrantiesList warranties={myWarranties} navigate={navigate} i18n={i18n} />
          </TabsContent>

          <TabsContent value="fotos" className="mt-0">
            <MyPhotosGrid photos={photos} setLightbox={setLightbox} downloadFromUrl={downloadFromUrl} i18n={i18n} />
          </TabsContent>

          <TabsContent value="projecten" className="mt-0">
            <MyProjectsList requests={requests} navigate={navigate} i18n={i18n} t={t} />
          </TabsContent>
        </Tabs>
      </div>

      <PhotoLightbox
        photos={photos}
        lightbox={lightbox}
        setLightbox={setLightbox}
        downloadFromUrl={downloadFromUrl}
      />
    </div>
  );
};

export default ClientDashboard;
